import { useState } from 'react'
import { AlertCircle, CheckCircle2, Info, Play } from 'lucide-react'
import { apiClient } from '../api/client'
import type { ValidationReport, UUID, ValidationFinding } from '../types'
import StatusBadge from './ui/StatusBadge'
import EmptyState from './ui/EmptyState'

interface Props {
  studyId: UUID
}

const levelVariant: Record<string, 'error' | 'warning' | 'info'> = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
}

export default function StudyValidationTab({ studyId }: Props) {
  const [report, setReport] = useState<ValidationReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleValidate = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.post<ValidationReport>(`/studies/${studyId}/validate`, {})
      setReport(res)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Validation failed')
    } finally {
      setLoading(false)
    }
  }

  const blockingCount = report?.findings.filter((f) => f.level === 'ERROR').length ?? 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Validation</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Check study completeness and conformance before export.
          </p>
        </div>
        <button
          onClick={handleValidate}
          disabled={loading}
          className="btn-primary"
        >
          <Play className="h-4 w-4 mr-1.5" aria-hidden={true} />
          {loading ? 'Running…' : 'Run Validation'}
        </button>
      </div>

      {error && (
        <div className="panel p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden={true} />
          <div className="text-sm">{error}</div>
        </div>
      )}

      {report && (
        <div className="space-y-6">
          <div
            className={`panel p-4 flex items-start gap-3 ${
              report.can_export
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}
          >
            {report.can_export ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" aria-hidden={true} />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" aria-hidden={true} />
            )}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {report.can_export
                  ? 'Study passed validation and is ready for export'
                  : `Export blocked: ${blockingCount} blocking issue${blockingCount === 1 ? '' : 's'} must be resolved`}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {report.error_count} errors · {report.warning_count} warnings · {report.info_count} info
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard value={report.error_count} label="Errors" variant="error" />
            <StatCard value={report.warning_count} label="Warnings" variant="warning" />
            <StatCard value={report.info_count} label="Info" variant="info" />
          </div>

          {report.findings.length > 0 ? (
            <div className="panel overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Findings</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Level</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Message</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Entity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Suggested Fix</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {report.findings.map((f, i) => (
                      <FindingRow key={i} finding={f} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <EmptyState
              icon={CheckCircle2}
              title="No issues found"
              description="Validation passed. The study is ready for export."
            />
          )}
        </div>
      )}
    </div>
  )
}

function FindingRow({ finding: f }: { finding: ValidationFinding }) {
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
      <td className="px-4 py-3 text-sm">
        <StatusBadge status={f.level} variant={levelVariant[f.level] || 'info'} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{f.category}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{f.message}</td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        {f.entity_type && f.entity_id ? `${f.entity_type}: ${f.entity_id}` : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
        {f.suggestion ? (
          <span className="inline-flex items-center gap-1">
            <Info className="h-4 w-4 text-primary-500" aria-hidden={true} />
            {f.suggestion}
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  )
}

function StatCard({
  value,
  label,
  variant,
}: {
  value: number
  label: string
  variant: 'error' | 'warning' | 'info'
}) {
  const colors = {
    error: 'text-red-700 dark:text-red-300',
    warning: 'text-amber-700 dark:text-amber-300',
    info: 'text-blue-700 dark:text-blue-300',
  }
  return (
    <div className="panel p-4 text-center">
      <div className={`text-2xl font-bold ${colors[variant]}`}>{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</div>
    </div>
  )
}
